import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env, FIRST_PAGE } from '@noor/config';
import {
  integrationTokenSearchSchema,
  merchantQueries,
  type IntegrationTokenSearch,
} from '@/entities/merchant';
import { IntegrationTokensPage } from '@/pages/integration-tokens';

/** Integration-tokens route — scope/status default to business/not_migrated. */
export const Route = createFileRoute('/integration-tokens')({
  staticData: { permission: 'integration_tokens.view' },
  beforeLoad: () => {
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: integrationTokenSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    const limit = deps.pageSize;
    void context.queryClient
      .prefetchQuery(
        merchantQueries.integrationTokens({
          scope: deps.scope,
          status: deps.status,
          phone: deps.phone,
          limit,
          offset: (deps.page - 1) * limit,
        }),
      )
      .catch(() => undefined);
  },
  component: IntegrationTokensRoute,
});

function IntegrationTokensRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const onParamsChange = (next: Partial<IntegrationTokenSearch>) => {
    const resetPage = !('page' in next) && search.page !== FIRST_PAGE;
    void navigate({
      search: (prev) => ({
        ...prev,
        ...next,
        ...(resetPage ? { page: FIRST_PAGE } : {}),
      }),
    });
  };

  return <IntegrationTokensPage params={search} onParamsChange={onParamsChange} />;
}
